import { SCAN_STAGES, type ScanStageKey, stageIndex } from './scanStages';

export interface ScanToCadProgressProps {
  /** Current pipeline stage key, or null before the first stage. */
  stage: ScanStageKey | null;
  /** Overall 0–1 progress across the pipeline (REAL, from the backend). */
  progress: number;
  message?: string;
}

// Generation progress mapped to the recon pipeline's ACTUAL stages — a stepper
// (which of the six stages is running / done) plus an overall bar driven by the
// real backend fraction. Unlike the image-to-CAD timer animation, every fill
// here reflects real pipeline state.
export function ScanToCadProgress({
  stage,
  progress,
  message,
}: ScanToCadProgressProps) {
  const current = stageIndex(stage);
  const pct = Math.round(Math.min(1, Math.max(0, progress)) * 100);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-telekinesis-neutral-50">
          {message ?? 'Reconstructing scan → STEP…'}
        </p>
        <span className="text-[11px] tabular-nums text-[#107d40]">{pct}%</span>
      </div>

      <div className="h-1.5 w-full overflow-hidden rounded-full bg-telekinesis-neutral-800">
        <div
          className="h-full rounded-full bg-[#107d40] transition-[width] duration-200 ease-out"
          style={{ width: `${Math.max(3, pct)}%` }}
        />
      </div>

      <ol className="space-y-1.5 pt-1">
        {SCAN_STAGES.map((s, i) => {
          const done = current > i;
          const active = current === i;
          return (
            <li key={s.key} className="flex items-center gap-2 text-[11px]">
              <span
                aria-hidden
                className={`flex h-4 w-4 flex-none items-center justify-center rounded-full border text-[9px] ${
                  done
                    ? 'border-[#107d40] bg-[#107d40] text-black'
                    : active
                      ? 'border-[#107d40] text-[#107d40]'
                      : 'border-telekinesis-neutral-700 text-telekinesis-neutral-400'
                }`}
              >
                {done ? '✓' : i + 1}
              </span>
              <span
                className={
                  done
                    ? 'text-telekinesis-neutral-400 line-through'
                    : active
                      ? 'text-[#107d40]'
                      : 'text-telekinesis-neutral-50'
                }
              >
                {s.label}
              </span>
              {active && (
                <span className="ml-1 inline-block h-1 w-1 animate-pulse rounded-full bg-[#107d40]" />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
